import { Suspense, useState, type ReactNode } from 'react';
import { Package, RefreshCw, TriangleAlert } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { cn } from '@worldcoo/ui/lib/utils';

export function DashboardSectionEmptyState(props: {
	title: string;
	description: string;
	action?: ReactNode;
	className?: string;
}) {
	return (
		<div className={cn('flex flex-col items-center justify-center gap-2 px-4 text-center', props.className)}>
			<div className="flex size-12 items-center justify-center rounded-xl bg-[#ffe4d0]">
				<Package className="size-6 text-[#f66900]" strokeWidth={1.5} />
			</div>
			<p className="text-foreground text-sm font-medium">{props.title}</p>
			<p className="text-muted-foreground text-xs">{props.description}</p>
			{props.action ? <div className="mt-2">{props.action}</div> : null}
		</div>
	);
}

export function DashboardSectionErrorState(props: {
	title: string;
	description?: string;
	onRetry: () => void;
	isRetrying?: boolean;
	className?: string;
}) {
	const { t } = useLingui();
	return (
		<div className={cn('flex flex-col items-center justify-center gap-2 px-4 text-center', props.className)}>
			<div className="flex size-12 items-center justify-center rounded-xl bg-[#f1f2f1]">
				<TriangleAlert className="size-6 text-[#8a8f8a]" strokeWidth={1.5} />
			</div>
			<p className="text-foreground text-sm font-medium">{props.title}</p>
			<p className="text-muted-foreground text-xs">{props.description ?? t`Try again or check back shortly.`}</p>
			<Button variant="outline-dark" onClick={props.onRetry} disabled={props.isRetrying} className="mt-3">
				<RefreshCw className={cn('size-3.5', props.isRetrying && 'animate-spin')} strokeWidth={1.8} />
				{t`Retry`}
			</Button>
		</div>
	);
}

/**
 * Wraps a dashboard section in a Suspense + ErrorBoundary so it loads, fails,
 * and retries independently from the rest of the dashboard. `errorRender` lets
 * the caller place the error state inside the section's own Card so the title
 * stays visible while the body shows the error UI.
 *
 * Retry: when `retryQueryKey` is provided, clicking Retry triggers
 * `queryClient.refetchQueries` and keeps the error UI mounted (with the
 * button disabled and a spinning icon) until the refetch settles, then
 * resets the boundary so a successful response renders the data and a
 * still-failing one re-shows the error UI. Without `retryQueryKey` the
 * retry button is a plain reset (used by the campaigns download action,
 * which has no visible button).
 */
export function DashboardSectionBoundary(props: {
	fallback: ReactNode;
	errorRender: (retry: () => void, isRetrying: boolean) => ReactNode;
	retryQueryKey?: readonly unknown[];
	children: ReactNode;
}) {
	const { reset } = useQueryErrorResetBoundary();
	return (
		<ErrorBoundary
			onReset={reset}
			fallbackRender={({ resetErrorBoundary }) => (
				<DashboardSectionErrorFallback
					resetErrorBoundary={resetErrorBoundary}
					retryQueryKey={props.retryQueryKey}
					render={props.errorRender}
				/>
			)}
		>
			<Suspense fallback={props.fallback}>{props.children}</Suspense>
		</ErrorBoundary>
	);
}

function DashboardSectionErrorFallback(props: {
	resetErrorBoundary: () => void;
	retryQueryKey?: readonly unknown[];
	render: (retry: () => void, isRetrying: boolean) => ReactNode;
}) {
	const queryClient = useQueryClient();
	const [isRetrying, setIsRetrying] = useState(false);

	const retry = async () => {
		if (!props.retryQueryKey) {
			props.resetErrorBoundary();
			return;
		}
		setIsRetrying(true);
		await queryClient.refetchQueries({ queryKey: props.retryQueryKey });
		const state = queryClient.getQueryState(props.retryQueryKey);
		setIsRetrying(false);
		// Only reset the boundary on a successful refetch — a still-failing
		// query keeps the existing error UI mounted (with the button just
		// re-enabled) instead of looping through Suspense's skeleton on the
		// inevitable re-throw from useSuspenseQuery.
		if (state?.status === 'success') {
			props.resetErrorBoundary();
		}
	};

	return <>{props.render(retry, isRetrying)}</>;
}
